import React, { FormEvent } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

interface TextNumberField {
  label: string;
  type: "text" | "number";
  value: string | number;
  required: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  subtitle?: string;
}
interface AutocompleteField<T> {
  label: string;
  type: "autocomplete";
  value: T | null;
  required: boolean;
  onChangeAutocomplete: (event: React.SyntheticEvent, value: T | null) => void;
  options: T[];
  getOptionLabel: (option: T) => string;
  multiple?: boolean;
  subtitle?: string;
}
interface CustomField {
  label: string;
  type: "custom";
  component: JSX.Element;
}

interface CheckboxField {
  label: string;
  type: "checkbox";
  value: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export type FormField =
  | TextNumberField
  | AutocompleteField<any>
  | CustomField
  | CheckboxField;

interface FormProps {
  title: string;
  fields: FormField[];
  onSubmit: (e: FormEvent) => Promise<void>;
  onBack: () => void;
  loading: boolean;
  secondaryAction?: {
    label: string;
    onClick: () => void | Promise<void>;
    disabled?: boolean;
    color?: "primary" | "secondary" | "error";
  };
}

const Formulario: React.FC<FormProps> = ({
  title,
  fields,
  onSubmit,
  onBack,
  loading,
  secondaryAction,
}) => {
  return (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">{title}</Typography>
          </Box>
          <form onSubmit={onSubmit}>
            {fields.map((field, index) => {
              if (field.type === "autocomplete") {
                return (
                  <Autocomplete
                    key={index}
                    multiple={field.multiple}
                    options={field.options}
                    getOptionLabel={field.getOptionLabel}
                    onChange={field.onChangeAutocomplete}
                    noOptionsText="Nenhuma opção encontrada!"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        variant="outlined"
                        fullWidth
                        required={field.required}
                        sx={{ mb: 2 }}
                        helperText={field.subtitle}
                      />
                    )}
                    value={field.value}
                  />
                );
              } else if (field.type === "custom") {
                return field.component;
              } else if (field.type === "checkbox") {
                return (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    }
                    label={field.label}
                    sx={{ mb: 2 }}
                  />
                );
              }
              return (
                <TextField
                  key={index}
                  label={field.label}
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 2 }}
                  type={field.type}
                  value={field.value}
                  onChange={field.onChange}
                  helperText={(field as TextNumberField).subtitle}
                  required={field.required}
                />
              );
            })}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
              {secondaryAction && (
                <Button
                  type="button"
                  variant="contained"
                  color={secondaryAction.color || "secondary"}
                  onClick={secondaryAction.onClick}
                  disabled={secondaryAction.disabled}
                >
                  {secondaryAction.label}
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? "Carregando..." : "Adicionar"}
              </Button>
            </Box>
          </form>
        </>
      )}
    </Box>
  );
};

export default Formulario;
